import React from 'react';
import { dayKey, getDeskStatus } from '../../../helpers/desk-booking';
import type { BookingIndex } from '../../../helpers/desk-booking';
import type { Desk } from '../../../types';

type Props = {
  desks: Array<Desk>,
  date: string,
  index: BookingIndex,
  targetUserId: number,
  selectedDeskId: number | null,
  picking: boolean,
  checkedDeskIds: Array<number> | null,
  canSelectInactive: boolean,
  onSelectDesk: (deskId: number | null) => void,
  onPickDesk: (deskId: number) => void,
  onToggleDesk: (deskId: number) => void,
};

type DeskPosition = { x: number, y: number, width: number, height: number };

// Simplified trace of the office floor plan (viewBox 0 0 870 970).
const WALLS = [
  'M5,5 H755 V595 H865 V965 H830',
  'M740,965 H337 V915 H5 V5',
  'M450,5 V430',
  'M5,325 H337 V445',
  'M337,545 V915',
  'M337,430 H355',
  'M440,430 H465',
  'M545,430 H755',
];

// Where each bookable desk is drawn, keyed by desk label. B desks are 2 columns × 3 rows,
// odd numbers on the left. Desks without a position here are not drawn.
const DESK_POSITIONS: Record<string, DeskPosition> = {
  A1: { x: 530, y: 95, width: 145, height: 75 },
  A2: { x: 530, y: 170, width: 75, height: 130 },
  A3: { x: 605, y: 170, width: 70, height: 130 },
  ...Object.fromEntries([1, 2, 3, 4, 5, 6].map((n) => [`B${n}`, {
    x: 96 + ((n - 1) % 2) * 75,
    y: 443 + Math.floor((n - 1) / 2) * 120,
    width: 75,
    height: 115,
  }])),
};

// Desks of the non-bookable office, drawn for orientation only.
const STATIC_DESKS = [
  { x: 150, y: 95, width: 130, height: 75 },
  { x: 280, y: 95, width: 75, height: 145 },
  { x: 150, y: 170, width: 130, height: 70 },
];

const FloorPlan = (props: Props): React.ReactElement => {
  const {
    desks, date, index, targetUserId, selectedDeskId, picking, checkedDeskIds, canSelectInactive, onSelectDesk, onPickDesk, onToggleDesk,
  } = props;
  const checking = !!checkedDeskIds;
  // Admins can open an unbookable desk to make it bookable again.
  const isSelectable = (desk: Desk) => desk.active || canSelectInactive;

  const handleSelect = (desk: Desk) => {
    if (!isSelectable(desk)) return;
    // While picking a desk for a day from "My days", only free desks react and book straight away.
    if (picking) {
      if (getDeskStatus(desk, date, index, targetUserId) === 'free') onPickDesk(desk.id);

      return;
    }
    if (checking) {
      if (desk.active) onToggleDesk(desk.id);

      return;
    }
    onSelectDesk(desk.id === selectedDeskId ? null : desk.id);
  };

  return (
    <svg className={`floor-plan${picking ? ' floor-plan--picking' : ''}${checking ? ' floor-plan--checking' : ''}${canSelectInactive ? ' floor-plan--manage' : ''}`} viewBox="0 0 870 970" role="img" aria-label="Office floor plan">
      <defs>
        <pattern id="desk-blocked-pattern" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="12" height="12" fill="#e9ecef" />
          <rect width="6" height="12" fill="#ced4da" />
        </pattern>
      </defs>
      <g className="floor-plan__static">
        <polygon className="floor-plan__room floor-plan__room--disabled" points="5,5 450,5 450,430 337,430 337,325 5,325" />
        <rect className="floor-plan__room" x={450} y={5} width={305} height={425} />
        <rect className="floor-plan__room" x={5} y={325} width={332} height={590} />
        <polygon className="floor-plan__room floor-plan__room--hall" points="337,430 755,430 755,595 865,595 865,965 337,965" />
        <rect className="floor-plan__service" x={337} y={630} width={243} height={260} />
        <rect className="floor-plan__service" x={585} y={630} width={60} height={250} />

        {STATIC_DESKS.map((desk) => (
          <rect key={`${desk.x}-${desk.y}`} className="floor-plan__static-desk" {...desk} />
        ))}
        <circle className="floor-plan__furniture" cx={640} cy={480} r={22} />
        <circle className="floor-plan__furniture" cx={705} cy={540} r={22} />
        <circle className="floor-plan__furniture" cx={815} cy={665} r={22} />
        <circle className="floor-plan__furniture" cx={815} cy={775} r={22} />

        {WALLS.map((d) => <path key={d} className="floor-plan__wall" d={d} />)}

        <text className="floor-plan__label" x={225} y={285}>URED</text>
        <text className="floor-plan__sublabel" x={225} y={308}>not bookable</text>
        <text className="floor-plan__label" x={602} y={395}>URED A</text>
        <text className="floor-plan__label" x={171} y={895}>URED B</text>
        <text className="floor-plan__label floor-plan__label--muted" x={458} y={765}>WC</text>
        <text className="floor-plan__label floor-plan__label--muted" x={615} y={755} transform="rotate(-90 615 755)">Kuhinja</text>
        <text className="floor-plan__sublabel" x={785} y={952}>Ulaz</text>
      </g>

      <g className="floor-plan__desks">
        {desks.map((desk) => {
          const position = DESK_POSITIONS[desk.label];
          if (!position) return null;

          const { x, y, width, height } = position;
          const status = getDeskStatus(desk, date, index, targetUserId);
          const booking = index.byDeskDate[dayKey(desk.id, date)];
          const block = index.blocksByDeskDate[dayKey(desk.id, date)];
          const selected = !picking && !checking && desk.id === selectedDeskId;
          const checked = !!checkedDeskIds?.includes(desk.id);
          let title = `${desk.label} – free${picking ? ', click to book' : ''}`;
          if (status === 'inactive') title = `${desk.label} – unavailable`;
          else if (block) title = `${desk.label} – blocked: ${block.reason}`;
          else if (booking) title = `${desk.label} – ${booking.userName}`;

          return (
            <g
              key={desk.id}
              className={`desk desk--${status}${selected ? ' desk--selected' : ''}${checked ? ' desk--checked' : ''}`}
              role="button"
              tabIndex={isSelectable(desk) ? 0 : -1}
              aria-pressed={checking ? checked : selected}
              onClick={() => handleSelect(desk)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelect(desk);
                }
              }}
            >
              <title>{title}</title>
              <rect className="desk__shape" x={x} y={y} width={width} height={height} rx={4} />
              <text className="desk__label" x={x + 7} y={y + 18}>{desk.label}</text>
              {checked && (
                <g className="desk__check">
                  <circle cx={x + width - 14} cy={y + 14} r={10} />
                  <path d={`M${x + width - 19},${y + 14} l4,4 l7,-8`} />
                </g>
              )}
              {block && (
                <text className="desk__blocked" x={x + width / 2} y={y + height / 2 + 5}>
                  Blocked
                </text>
              )}
              {booking && (
                <text className="desk__initials" x={x + width / 2} y={y + height / 2 + 8}>
                  {booking.userInitials}
                </text>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default FloorPlan;
